import { Clipboard } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { DialogSubscriptionComponent } from '../../dialog/dialog-subscription.component';
import { DialogService } from '../../dialog/dialog.service';
import { DialogConfig, DialogResponse } from '../../dialog/dialog.types';
import { QuestionDefinition, QuestionValueChange } from '../../forms/form.types';
import { QuestionComponent } from '../../forms/question/question.component';
import { AlertService } from '../../shared/services/alert.service';
import { TextIconComponent } from '../../shared/text-icon/text-icon.component';
import { ReflectionService } from '../../shared/utilities/reflection.service';
import { TableActionComponent } from '../table-action/table-action.component';
import { ColumnClickComplete, ColumnClickEvent, ColumnDefinition, TableCellResponse } from '../table.types';
import { TableCellValueType } from './table-cell.types';

@Component({
    selector: 'app-table-cell',
    templateUrl: './table-cell.component.html',
    styleUrls: ['./table-cell.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class TableCellComponent extends DialogSubscriptionComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() column: ColumnDefinition;
    @Input() element: any;
    @Output() elementUpdateEvent: EventEmitter<TableCellResponse> = new EventEmitter<TableCellResponse>();
    @ViewChild('cellContainer', { read: ViewContainerRef }) cellContainer: ViewContainerRef;
    @ViewChild('cellWrapper', { read: ViewContainerRef }) cellWrapper: ViewContainerRef;
    private actionComponents: Array<ComponentRef<TableActionComponent>> = [];
    private questionComponent: ComponentRef<QuestionComponent>;
    private questionSubscription$: Subscription;
    private booleanAsIconComponent: ComponentRef<TextIconComponent>;
    private cellFunctions: Array<(value: any) => TableCellValueType> = [
        this.booleanAsYesNo,
        this.booleanAsIcon,
        this.valueAsYesNo,
        this.numberAsYesNo,
        this.valueAsDate,
        this.valueAsJson,
        this.actionsColumn,
        this.questionColumn,
        this.objectList,
        this.bulletList
    ];
    takingAction: boolean;
    constructor(
        private renderer2: Renderer2,
        private reflection: ReflectionService,
        private datePipe: DatePipe,
        private alert: AlertService,
        private clipboard: Clipboard,
        private dialogService: DialogService<TableCellResponse>
    ) {
        super();
    }

    ngOnInit(): void {}

    ngAfterViewInit(): void {
        this.renderCell();
    }

    ngOnDestroy(): void {
        if (this?.actionComponents?.length) {
            this.actionComponents.forEach((component) => {
                component.destroy();
            });
            this.actionComponents = [];
        }
        if (this?.booleanAsIconComponent) {
            this.booleanAsIconComponent.destroy();
        }
        if (this.questionSubscription$) {
            this.questionSubscription$.unsubscribe();
        }
        if (this.questionComponent) {
            this.questionComponent.destroy();
        }
        super.ngOnDestroy();
    }

    renderCell(): void {
        if (!this.cellContainer) {
            console.error('Cell container not found');
            return;
        }
        if (!this.cellWrapper) {
            console.error('Cell wrapper not found.');
            return;
        }
        if (this.column.clickCallBack) {
            this.renderer2.addClass(this.cellWrapper.element.nativeElement, 'clickable');
            this.renderer2.listen(this.cellWrapper.element.nativeElement, 'click', this.onClick.bind(this));
        }
        if (this.column.clipBoardCopy) {
            this.renderer2.addClass(this.cellWrapper.element.nativeElement, 'clipboard');
            this.renderer2.listen(this.cellWrapper.element.nativeElement, 'click', this.copyToClipboard.bind(this));
        }
        if (this.column.showDialog) {
            this.renderer2.addClass(this.cellWrapper.element.nativeElement, 'clickable');
            this.renderer2.listen(this.cellWrapper.element.nativeElement, 'click', this.show.bind(this));
        }
        if (this.column.underlineBlue) {
            this.renderer2.addClass(this.cellWrapper.element.nativeElement, 'underline-blue');
        }
        if (this.column.cellAlignment) {
            let cellAlignment: string = '';
            switch (this.column.cellAlignment) {
                case 'center': {
                    cellAlignment = 'text-align-center';
                    break;
                }
                case 'right': {
                    cellAlignment = 'text-align-right';
                    break;
                }
            }
            this.renderer2.addClass(this.cellWrapper.element.nativeElement, cellAlignment);
        }
        this.computeValue();
    }

    computeValue() {
        let value = this.reflection.propertyByString(this.element, this.column.property);
        const append = true;
        let typeValue: TableCellValueType = null;
        this.cellFunctions.forEach((func) => {
            if (typeValue !== null) {
                return;
            }
            const thisTypeValue = func.call(this, value);
            if (thisTypeValue !== null) {
                typeValue = thisTypeValue;
            }
        });
        if (typeValue !== null && !typeValue.doNotAppend) {
            value = typeValue.value;
        }
        value = value === undefined ? '' : value;
        if (typeValue === null || !typeValue.doNotAppend) {
            const element = this.column.isJson ? 'pre' : 'div';
            const div = this.renderer2.createElement(element);
            div.innerHTML = value;
            this.renderer2.appendChild(this.cellWrapper.element.nativeElement, div);
        }
    }

    private bulletList(value: any): TableCellValueType {
        if (this.column.bulletList) {
            if (Array.isArray(value)) {
                const ul = this.renderer2.createElement('ul');
                value.forEach((v) => {
                    const li = this.renderer2.createElement('li');
                    li.innerHTML = v;
                    this.renderer2.appendChild(ul, li);
                });
                this.renderer2.appendChild(this.cellWrapper.element.nativeElement, ul);
                return {
                    value: ul,
                    originalValue: value,
                    doNotAppend: true
                };
            }
            return null;
        }
        return null;
    }

    private booleanAsYesNo(value: any): TableCellValueType {
        if (this.column.booleanAsYesNo) {
            return {
                value: value === true ? 'Yes' : 'No',
                originalValue: value
            };
        }
        return null;
    }

    private valueAsYesNo(value: any): TableCellValueType {
        if (this.column.valueAsYesNo) {
            return {
                value: this.reflection.isNotNullOrEmpty(value) ? 'Yes' : 'No',
                originalValue: value
            };
        }
        return null;
    }

    private numberAsYesNo(value: any): TableCellValueType {
        if (this.column.numberAsYesNo) {
            return {
                value: value === '0' || value === 0 ? 'No' : 'Yes',
                originalValue: value
            };
        }
        return null;
    }

    private booleanAsIcon(value: any): TableCellValueType {
        if (this.column.booleanAsIcon) {
            const component = this.cellContainer.createComponent(TextIconComponent);
            if (value === true) {
                component.instance.icon = this.column.booleanAsIcon.trueIcon;
                component.instance.tooltip = this.reflection.stringDataMerge(
                    this.element,
                    this.column.booleanAsIcon.trueToolTip
                );
            } else {
                component.instance.icon = this.column.booleanAsIcon.falseIcon;
                component.instance.tooltip = this.reflection.stringDataMerge(
                    this.element,
                    this.column.booleanAsIcon.falseToolTip
                );
            }
            component.changeDetectorRef.detectChanges();
            this.booleanAsIconComponent = component;
            return {
                value: value,
                originalValue: value,
                doNotAppend: true
            };
        }
        return null;
    }

    private valueAsDate(value: any): TableCellValueType {
        if (this.column.isDate) {
            return {
                value: this.datePipe.transform(value, this.column.dateFormat),
                originalValue: value
            };
        }
        return null;
    }

    private valueAsJson(value: any): TableCellValueType {
        if (this.column.isJson) {
            return {
                value: JSON.stringify(value, undefined, 2),
                originalValue: value
            };
        }
        return null;
    }

    private actionsColumn(value: any): TableCellValueType {
        if (this.column.actions) {
            this.renderer2.addClass(this.cellWrapper.element.nativeElement, 'action-buttons');
            for (const action of this.column.actions) {
                const component = this.cellContainer.createComponent(TableActionComponent);
                component.instance.element = this.element;
                component.instance.action = action;
                component.changeDetectorRef.detectChanges();
                this.actionComponents.push(component);
            }
            return {
                value: value,
                originalValue: value,
                doNotAppend: true
            };
        }
        return null;
    }

    private objectList(value: any): TableCellValueType {
        if (this.column.objectList) {
            const ul = this.renderer2.createElement('ul');
            if (Array.isArray(value)) {
                value.forEach((v) => {
                    const li = this.renderer2.createElement('li');
                    li.innerHTML = this.reflection.stringDataMerge(v, this.column.objectList.interpolate);
                    if (this.column.objectList.clickCallBack) {
                        this.renderer2.addClass(li, 'clickable');
                        this.renderer2.listen(li, 'click', ($event) => {
                            const complete: ColumnClickComplete = this.column.objectList.clickCallBack.function.call(
                                this.column.objectList.clickCallBack.this,
                                {
                                    element: this.element,
                                    column: this.column,
                                    event: $event,
                                    object: v
                                } as ColumnClickEvent<any>
                            );
                            this.completeClick(complete);
                        });
                    }
                    this.renderer2.appendChild(ul, li);
                });
            }
            this.renderer2.appendChild(this.cellWrapper.element.nativeElement, ul);
            return {
                value: ul,
                originalValue: value,
                doNotAppend: true
            };
        }
        return null;
    }

    private questionColumn(value: any): TableCellValueType {
        if (this.column.question) {
            const cellQuestion: QuestionDefinition = cloneDeep(this.column.question);
            cellQuestion.input.property = this.reflection.stringDataMerge(this.element, cellQuestion.input.property);
            cellQuestion.input.control = new UntypedFormControl(value);
            this.questionComponent = this.cellContainer.createComponent(QuestionComponent);
            this.questionComponent.instance.question = cellQuestion;
            this.questionSubscription$ = this.questionComponent.instance.controlValueChange.subscribe(
                (v: QuestionValueChange) => {
                    if (this.column.onChangeSubmitTo) {
                        if (this.column.onChangeSubmitTo.observable) {
                            this.reflection.setPropertyByString(this.element, this.column.property, v.payload);
                            this.column.onChangeSubmitTo.observable
                                .call(this.column.onChangeSubmitTo.this, this.element, v.question)
                                .subscribe({
                                    next: (next) => {
                                        this.column.onChangeSubmitTo.function(this.element, next, v.question);
                                    },
                                    error: (error) => {
                                        this.alert.toastErrorObject(error);
                                    }
                                });
                        }
                    }
                }
            );
            return {
                value: value,
                originalValue: value,
                doNotAppend: true
            };
        }
        return null;
    }

    copyToClipboard() {
        this.clipboard.copy(this.element[this.column.property]);
        this.alert.toastSuccess(`${this.element[this.column.property]} copied to clipboard.`);
    }

    show() {
        this.takingAction = true;
        const config: DialogConfig = Object.assign(
            Object.create(this.column.showDialog.dialogConfig),
            this.column.showDialog.dialogConfig
        );
        config.title = this.reflection.stringDataMerge(this.element, config.title);
        config.uniqueId = this.reflection.stringDataMerge(this.element, config.uniqueId);
        const dialog = this.dialogService.show(
            {
                element: this.element,
                configuration: this.column.showDialog.configuration
            } as TableCellResponse,
            config
        );
        this.addSubscription(
            this.dialogService.response.subscribe((next: DialogResponse<TableCellResponse>) => {
                if (this.column.showDialog.dialogConfig.dialogAlias === next.dialogAlias) {
                    this.elementUpdateEvent.emit(next.payload);
                }
            }),
            dialog
        );
        this.takingAction = false;
    }

    onClick($event) {
        this.takingAction = true;
        if (this.column.clickCallBack.observable) {
            this.column.clickCallBack.observable
                .call(this.column.clickCallBack.this, {
                    element: this.element,
                    column: this.column
                } as ColumnClickEvent<any>)
                .subscribe(
                    (next) => {
                        const complete: ColumnClickComplete = this.column.clickCallBack.function.call(
                            this.column.clickCallBack.this,
                            {
                                element: this.element,
                                observableValue: next,
                                column: this.column,
                                event: $event
                            } as ColumnClickEvent<any>
                        );
                        this.completeClick(complete);
                    },
                    (error) => {
                        this.alert.toastError(this.column.clickCallBack.errorMessage);
                        console.error(error);
                    }
                );
        } else {
            const complete: ColumnClickComplete = this.column.clickCallBack.function.call(
                this.column.clickCallBack.this,
                {
                    element: this.element,
                    column: this.column,
                    event: $event
                } as ColumnClickEvent<any>
            );
            this.completeClick(complete);
        }
    }

    completeClick(complete: ColumnClickComplete) {
        if (complete?.completed) {
            if (this.column.booleanAsIcon) {
                if (complete.value === true) {
                    this.booleanAsIconComponent.instance.icon = this.column.booleanAsIcon.trueIcon;
                    this.booleanAsIconComponent.instance.tooltip = this.reflection.stringDataMerge(
                        this.element,
                        this.column.booleanAsIcon.trueToolTip
                    );
                } else {
                    this.booleanAsIconComponent.instance.icon = this.column.booleanAsIcon.falseIcon;
                    this.booleanAsIconComponent.instance.tooltip = this.reflection.stringDataMerge(
                        this.element,
                        this.column.booleanAsIcon.falseToolTip
                    );
                }
            }
        }
        this.takingAction = false;
    }

    stringDataMerge(element: any, column: ColumnDefinition) {
        if (this.reflection.isNotNullOrEmpty(this.element)) {
            return this.reflection.stringDataMerge(element, column.cellTooltip);
        }
        return '';
    }
}
